import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import DialogContent from '@mui/material/DialogContent';
import { Box, Divider, Stack, TextField, Typography } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import _ from 'lodash';

import { SelectFieldMultipleFormikWithMore } from '../../../../../common/select_field';
import { AutocompleteMultipleFormik } from '../../../../../common/autocomplete';
import { getProductBatchesState } from '../../../../../../features/inventory/get_product_batches';
import useNotification from '../../../../../../core/hooks/use_notification';
import { getAllPodsState } from '../../../../../../features/pod/get_all_pods';
import { Button, SubmitButton } from '../../../../../common/button';
import TextFieldFormik from '../../../../../common/text_field';
import { createOrUpdateProductExpirationPromotionsAction, createOrUpdateProductExpirationPromotionsState } from '../../../../../../features/price/create_or_update_product_expiration_promotions';
import { updatePromotionNotificationData } from '../../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../../core/enums/common/api';
import { getPriceState } from '../../../../../../features/price/get_price';
import { parseDateString } from '../../../../../../core/utils/date_utils';
import { DateTimePickerFormik } from '../../../../../common/date_picker';
import { MISC_DATE_ISO_FORMAT } from '../../../../../../core/constants';

const PromotionsFormDialog = ({ productId, refreshList }) => {
    const { showNotification } = useNotification();
    const dispatch = useDispatch();

    const getAllPods = useSelector(getAllPodsState);
    const getPrice = useSelector(getPriceState);
    const getProductBatches = useSelector(getProductBatchesState);
    const createOrUpdateExpirationPromotions = useSelector(createOrUpdateProductExpirationPromotionsState);

    const [open, setOpen] = useState(false);
    const [startPrice, setStartPrice] = useState(0.00);
    const [discountPrice, setDiscountPrice] = useState(0.00);

    const mapProductBatchOptions = useCallback((data) => {
        return data?.map(productBatch => ({
            key: productBatch?.id + Math.random(),
            value: productBatch?.productBatchId,
            label: `${ productBatch?.productBatchId }, nr magazynowy: ${ productBatch?.warehouseBatchNumber }, cena zakupu: ${ productBatch?.purchaseGrossPrice?.toFixed(2) }`,
            expiryDate: productBatch?.expiryDate
        }));
    }, []);

    const productBatchOptions = [...mapProductBatchOptions(getProductBatches?.data)];

    const calculateStartingPrice = (podIds, discount) => {
        const minPrice = Math.min(...podIds?.map(podId => [getPrice?.data?.find(item => item.podId === podId)].map(item => item.promotionGrossPrice?.toFixed(2) || item.basicGrossPrice?.toFixed(2))[0]) || 0.00);
        setStartPrice(minPrice);
        discountPrice !== 0.00 && setDiscountPrice((parseFloat(minPrice).toFixed(2) - parseFloat((discount / 100 * minPrice).toFixed(2))).toFixed(2));
    };

    const initialValues = {
        podIds: [],
        productBatches: [],
        timeFrom: '',
        timeTo: '',
        discount: ''
    };

    const schema = Yup.object().shape({
        podIds: Yup.array().min(1, 'Proszę wybrać co najmniej jeden punkt'),
        productBatches: Yup.array().min(1, 'Proszę wybrać co najmniej jedną partię'),
        timeFrom: Yup.date()
            .transform(parseDateString)
            .nullable()
            .required('Data od jest wymagana'),
        timeTo: Yup.date()
            .transform(parseDateString)
            .nullable()
            .required('Data do jest wymagana'),
        discount: Yup.number().required('Proszę podać wartość upustu')
    });

    const onClose = () => setOpen(false);

    const onSubmit = (values) => {
        const timeFrom = values?.timeFrom?.format(MISC_DATE_ISO_FORMAT);
        const timeTo = values?.timeTo?.format(MISC_DATE_ISO_FORMAT);

        const form = {
            ..._.omit(values, ['productBatches', 'timeFrom', 'timeTo']),
            productBatchIds: values.productBatches.map(productBatch => productBatch.value),
            timeFrom,
            timeTo
        };

        dispatch(createOrUpdateProductExpirationPromotionsAction({ productId, form }))
            .then(response => {
                showNotification(updatePromotionNotificationData(response));
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    refreshList && refreshList();
                    onClose();
                }
            });
    };

    return (
        <>
            <Button onClick={ () => setOpen(true) }>
                Dodaj promocje
            </Button>
            <Dialog open={ open } onClose={ onClose } fullWidth={ true } maxWidth="md">
                <Formik
                    initialValues={ initialValues }
                    validationSchema={ schema }
                    validateOnChange={ false }
                    validateOnBlur={ false }
                    onSubmit={ onSubmit }>
                    { (formik) => (
                        <Box component={ Form } sx={ { display: 'flex', flexDirection: 'column' } }>
                            <DialogTitle>
                                <Typography variant="h6" fontWeight="bold">
                                    Promocje na krótką datę przydatności
                                </Typography>
                            </DialogTitle>
                            <Divider/>

                            <DialogContent>
                                <Stack spacing={ 4 } sx={ { py: 1 } }>
                                    <Box>
                                        <SelectFieldMultipleFormikWithMore
                                            name="podIds"
                                            label="Wybierz pody - wiele"
                                            defaultOptions={ getAllPods?.data?.filter(pod => pod?.device?.serviceId != null).map(pod => (
                                                {
                                                    value: pod?.id,
                                                    label: pod?.indoorPartner?.name + ' (' + pod?.description + ') - ' + pod?.device?.serviceId + ', aktualna cena: '
                                                        + ([getPrice?.data?.find(item => item.podId === pod?.id)].map(item => item.promotionGrossPrice?.toFixed(2) || item.basicGrossPrice?.toFixed(2))[0] || '-')
                                                }
                                            )) }
                                            extraOptions={ getAllPods?.data?.filter(pod => pod?.device?.serviceId == null).map(pod => (
                                                {
                                                    value: pod?.id,
                                                    label: pod?.indoorPartner?.name + ' (' + pod?.description + ') - brak urządzenia, aktualna cena: '
                                                        + ([getPrice?.data?.find(item => item.podId === pod?.id)].map(item => item.promotionGrossPrice?.toFixed(2) || item.basicGrossPrice?.toFixed(2))[0] || '-')
                                                }
                                            )) }
                                            required
                                            onChange={ (value) => calculateStartingPrice(value, formik.values.discount) }
                                        />
                                    </Box>

                                    <Box>
                                        <AutocompleteMultipleFormik
                                            options={ productBatchOptions }
                                            name="productBatches"
                                            label="Wybierz partie produktu - wiele"
                                            required
                                            groupBy={ option => option.expiryDate }
                                        />
                                    </Box>

                                    <Box sx={ {
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                                        gap: 3
                                    } }>
                                        <TextField
                                            fullWidth
                                            label="Aktualna cena produktu"
                                            type="number"
                                            disabled
                                            value={ startPrice?.toFixed(2) }
                                        />
                                        <TextFieldFormik
                                            name="discount"
                                            label="Upust [%]"
                                            type="number"
                                            required
                                            inputProps={ { min: '0.01', max: '100.00', step: '0.01' } }
                                            onChange={ (value) => setDiscountPrice((parseFloat(startPrice).toFixed(2) - parseFloat((value / 100 * startPrice).toFixed(2))).toFixed(2)) }
                                        />
                                        <TextField
                                            fullWidth
                                            label="Cena promocyjna"
                                            type="number"
                                            disabled
                                            value={ discountPrice }
                                        />
                                    </Box>

                                    <Box sx={ {
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                                        gap: 3
                                    } }>
                                        <DateTimePickerFormik
                                            name="timeFrom"
                                            label="Data od"
                                            required
                                            InputLabelProps={ { shrink: true } }
                                        />
                                        <DateTimePickerFormik
                                            name="timeTo"
                                            label="Data do"
                                            required
                                            InputLabelProps={ { shrink: true } }
                                        />
                                    </Box>
                                </Stack>
                            </DialogContent>

                            <Divider/>
                            <DialogActions sx={ { px: 3, py: 2, gap: 2 } }>
                                <Button variant="outlined" onClick={ onClose }>Anuluj</Button>
                                <SubmitButton isLoading={ createOrUpdateExpirationPromotions?.loading }>Zapisz
                                    promocję</SubmitButton>
                            </DialogActions>
                        </Box>
                    ) }
                </Formik>
            </Dialog>
        </>
    );
};

export default PromotionsFormDialog;